import React, { useState } from 'react';
import { useStore } from '../context/Store';
import Button from '../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const { FiPlus, FiExternalLink, FiCopy, FiTrash2, FiBarChart2 } = FiIcons;

const Dashboard = () => {
  const { user, domains, addDomain } = useStore();
  const [isAdding, setIsAdding] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  const handleAdd = (e) => {
    e.preventDefault();
    if (newDomain.trim()) {
      const success = addDomain(newDomain.trim());
      if (success) {
        setNewDomain('');
        setIsAdding(false);
      }
    }
  };

  const copyLink = (domain) => {
    const url = `${window.location.origin}/#/d/${domain}`;
    navigator.clipboard.writeText(url);
    setCopiedId(domain);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Your Domains</h1>
          <p className="text-gray-500 text-sm mt-1">Manage and track your domain sales.</p>
        </div>
        <Button onClick={() => setIsAdding(true)} className="gap-2 shadow-lg shadow-indigo-200">
          <SafeIcon icon={FiPlus} /> Add Domain
        </Button>
      </div>

      {/* Add Domain Modal/Inline Form */}
      <AnimatePresence>
        {isAdding && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-8 bg-white p-6 rounded-xl border border-indigo-100 shadow-sm overflow-hidden"
          >
            <form onSubmit={handleAdd} className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Domain Name</label>
                <input 
                  autoFocus
                  type="text" 
                  placeholder="example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                />
              </div>
              <Button type="submit">Generate Page</Button>
              <Button type="button" variant="ghost" onClick={() => setIsAdding(false)}>Cancel</Button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Domain List */}
      <div className="grid gap-4">
        {domains.length === 0 ? (
          <div className="text-center py-20 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
              <SafeIcon icon={FiPlus} className="w-8 h-8 text-indigo-300" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">No domains yet</h3>
            <p className="text-gray-500 mb-6">Add your first domain to start selling.</p>
            <Button onClick={() => setIsAdding(true)}>Add Domain</Button>
          </div>
        ) : (
          domains.map((domain) => (
            <motion.div 
              key={domain.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            >
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-bold text-gray-900">{domain.name}</h3>
                  <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">Active</span>
                </div>
                <a 
                  href={`/#/d/${domain.name}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-gray-500 hover:text-indigo-600 flex items-center gap-1 mt-1 w-fit"
                >
                  moloji.app/d/{domain.name} <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
                </a>
              </div>

              <div className="flex items-center gap-2 self-end sm:self-auto">
                <Button 
                  variant="secondary" 
                  className="text-sm h-9 px-3 gap-2"
                  onClick={() => copyLink(domain.name)}
                >
                  <SafeIcon icon={FiCopy} className="w-3.5 h-3.5" />
                  {copiedId === domain.name ? 'Copied!' : 'Copy Link'}
                </Button>
                {/* Placeholder for future delete/stats features */}
                {/* <Button variant="ghost" className="h-9 w-9 p-0 text-gray-400 hover:text-red-500">
                  <SafeIcon icon={FiTrash2} />
                </Button> */}
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default Dashboard;